/**
 * Embed bootstrap — the one request the whole widget depends on.
 *
 * `GET /api/deployments/embed/:id/public` is the first thing the iframe does
 * and everything else waits on it. It had no timeout and no retry, so a single
 * transient blip left the visitor on a customer's site looking at a dead
 * widget with no way back. This module owns that call's resilience and, just
 * as importantly, its DIAGNOSABILITY.
 *
 * ## Why a failure taxonomy rather than the browser's message
 *
 * `TypeError: Load failed` (Safari) and `TypeError: Failed to fetch` (Chrome)
 * are the same event — "the fetch never completed" — and neither says why. A
 * CORS refusal, a DNS failure, a TLS error, a request killed by a content
 * blocker and a visitor navigating away mid-flight all collapse to one of
 * those two strings. Triage from them is guesswork, which is why the reported
 * volume has never been separable into causes.
 */

import { type DeploymentResponse } from "@nexus/types"
import { ZDeployments } from "@nexus/types/contracts"

import { getBackendUrl } from "./config"

/** Why the embed bootstrap failed. */
export type EmbedBootstrapFailureReason =
  | "timeout" // our own deadline elapsed before a response arrived
  | "aborted" // the caller cancelled — in practice, the visitor left the page
  | "offline" // the browser reports no connectivity at all
  | "network" // the fetch never completed for any other reason
  | "http" // a response DID arrive, carrying a non-2xx status
  | "malformed" // a 2xx body that does not match the published contract

/**
 * A bootstrap failure that names its own cause.
 *
 * The `message` is deliberately OURS rather than the browser's, and that is
 * load-bearing for measurement rather than cosmetic. `sentry.client.config.ts`
 * ignores `Failed to fetch` (Chrome's wording) and does NOT ignore `Load
 * failed` (Safari's), so the identical defect is dropped on one browser and
 * reported on the other. That asymmetry is the whole reason this failure reads
 * as Safari-specific — the reported browser mix is a property of our ignore
 * list, not of the bug. A message neither entry matches is counted the same
 * way in every browser.
 */
export class EmbedBootstrapError extends Error {
  readonly reason: EmbedBootstrapFailureReason
  readonly status?: number
  /**
   * The underlying wording — the browser's, or the server's body.
   *
   * 🔴 THIS IS DELIBERATELY NOT IN `message`, AND INTERPOLATING IT BACK IN
   * REINSTATES THE BUG. `ignoreErrors` matches STRING entries by SUBSTRING and
   * regex entries UNANCHORED, so a message ending in the browser's own text is
   * matched just as surely as the bare text: `Embed bootstrap failed
   * (network): Failed to fetch` matches `/Failed to fetch/`, and Firefox's
   * `NetworkError …` matches the `"NetworkError"` entry. Chrome and Firefox
   * would be dropped while Safari's `Load failed` — which no entry matches —
   * is reported, which is precisely the browser skew this module exists to
   * remove.
   *
   * Sentry still receives it: the call site passes it through `extra`, which
   * `ignoreErrors` never inspects.
   */
  readonly detail: string

  constructor(reason: EmbedBootstrapFailureReason, detail: string, status?: number) {
    super(
      status === undefined
        ? `Embed bootstrap failed (${reason})`
        : `Embed bootstrap failed (${reason}, HTTP ${status})`
    )
    this.name = "EmbedBootstrapError"
    this.reason = reason
    this.status = status
    this.detail = detail
  }
}

/** How long one attempt may run before it is abandoned. */
export const BOOTSTRAP_TIMEOUT_MS = 8_000
/** Total attempts, the first one included. */
export const BOOTSTRAP_ATTEMPTS = 3
/** Base for the exponential backoff between attempts. */
export const BOOTSTRAP_BACKOFF_MS = 300

/**
 * Only a failure a later attempt could plausibly survive is retried.
 *
 * A 4xx, a malformed body and a caller that cancelled are all stable answers —
 * retrying them burns the visitor's time to arrive at the same place, and in
 * the `aborted` case the widget is already gone.
 */
export const isTransient = (reason: EmbedBootstrapFailureReason): boolean =>
  reason === "timeout" || reason === "network"

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

/** One bootstrap attempt, with every failure mapped onto a named reason. */
async function fetchPublicInfoOnce(
  deploymentId: string,
  signal: AbortSignal | undefined
): Promise<DeploymentResponse> {
  const url = ZDeployments.GetEmbedPublicInfo.path.replace(":id", deploymentId)
  const controller = new AbortController()
  const timeout = setTimeout(() => {
    controller.abort()
  }, BOOTSTRAP_TIMEOUT_MS)

  // The caller's own signal (an unmounting widget) must still win.
  const onCallerAbort = (): void => {
    controller.abort()
  }
  signal?.addEventListener("abort", onCallerAbort)

  let response: Response
  try {
    response = await fetch(`${getBackendUrl()}${url}`, {
      method: ZDeployments.GetEmbedPublicInfo.method,
      signal: controller.signal
      // NO `Content-Type` HEADER, DELIBERATELY. This is a GET with no body, so
      // the header describes nothing that exists — and `application/json` is
      // not a CORS-safelisted value, so sending it turned every single widget
      // load into a PREFLIGHT followed by the GET. That is two round trips
      // where one will do, and an OPTIONS request that any corporate proxy,
      // WAF or content blocker can drop on its own — producing exactly the
      // opaque, status-less failure this module exists to explain.
    })
  } catch (error) {
    if (signal?.aborted === true) {
      throw new EmbedBootstrapError("aborted", "the caller cancelled the request")
    }
    if (controller.signal.aborted) {
      throw new EmbedBootstrapError("timeout", `no response within ${BOOTSTRAP_TIMEOUT_MS}ms`)
    }
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      throw new EmbedBootstrapError("offline", "the browser reports no connectivity")
    }
    throw new EmbedBootstrapError("network", error instanceof Error ? error.message : String(error))
  } finally {
    clearTimeout(timeout)
    signal?.removeEventListener("abort", onCallerAbort)
  }

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error")
    throw new EmbedBootstrapError("http", errorText, response.status)
  }

  try {
    const json = await response.json()
    return ZDeployments.GetEmbedPublicInfo.Response.parse(json).data
  } catch (error) {
    throw new EmbedBootstrapError(
      "malformed",
      error instanceof Error ? error.message : String(error),
      response.status
    )
  }
}

/**
 * Fetch the embed's public configuration, retrying a transient failure.
 *
 * Makes up to {@link BOOTSTRAP_ATTEMPTS} attempts with exponential backoff,
 * and gives up immediately on a failure a retry cannot fix.
 *
 * @throws EmbedBootstrapError naming the reason it gave up
 */
export async function fetchEmbedPublicInfo(
  deploymentId: string,
  signal?: AbortSignal
): Promise<DeploymentResponse> {
  let lastError: EmbedBootstrapError | undefined

  for (let attempt = 1; attempt <= BOOTSTRAP_ATTEMPTS; attempt++) {
    try {
      return await fetchPublicInfoOnce(deploymentId, signal)
    } catch (error) {
      if (!(error instanceof EmbedBootstrapError)) {
        throw error
      }
      lastError = error

      if (!isTransient(error.reason) || attempt === BOOTSTRAP_ATTEMPTS) {
        throw error
      }
      await sleep(BOOTSTRAP_BACKOFF_MS * 2 ** (attempt - 1))
    }
  }

  // Unreachable: the loop either returns or throws. Kept so the function has a
  // total return type without a non-null assertion.
  throw lastError ?? new EmbedBootstrapError("network", "no attempt was made")
}
